/**
 * useStudentCloseAccountState — « Fermer mon compte » : la confirmation, puis la suppression.
 *
 * Port de `DeleteAccountScreen.js`, dans son ordre : après la confirmation, sur mobile les
 * notifications de l'appareil effacées, puis l'appareil désinscrit des notifications du compte
 * (`unsubscribeFromNotifications`, seulement si un jeton Expo est connu) et ENSUITE la suppression
 * (`deleteAccount`) ; sans jeton, la suppression directement. Son succès vide la session (les
 * réducteurs de `deleteAccount`) : l'app repart sur l'écran de connexion, rien à faire ici.
 *
 * Un échec de la désinscription arrête tout et se dit, sauf le statut `failed` (« le jeton n'existe
 * probablement pas »), qui laisse partir la suppression — la règle de l'ancien écran, recopiée telle
 * quelle : la tranche range le statut HTTP sous `status`, la branche ne se prend donc jamais
 * aujourd'hui. Un échec de la suppression se dit aussi (`<Snackbar>` au lieu de la boîte d'erreur).
 */
import * as Notifications from "expo-notifications"
import { useCallback, useReducer } from "react"
import { Platform } from "react-native"
import { useSelector } from "react-redux"

import { authService } from "../../../../../../data/services/authService"
import { trackDialogButton } from "../../../hooks/trackDialogButton"
import { useApiMutation } from "../../../hooks/useApiMutation"
import { useBackOr } from "../../../hooks/useBackOr"

/**
 * Le titre de l'ancienne confirmation : ses deux boutons sont mesurés sous ce nom
 * (`trackDialogButton`), « Annuler » et « Supprimer mon compte » comme dans `DeleteAccountScreen.js`.
 */
const CLOSE_DIALOG_TITLE = "Veux-tu vraiment fermer ton compte ?"

/** Le repli d'un lien ouvert à froid : Paramètres, sur l'onglet du compte. */
const SETTINGS_ACCOUNT_TAB = { initialTab: 3 }

const CLOSE_FAILED = "Ton compte n'a pas pu être fermé. Réessaie dans un instant."

type CloseAccountUi = { confirmVisible: boolean; closing: boolean; closeError: string | null }
type CloseAccountAction =
    | { type: "ASK" }
    | { type: "CANCEL" }
    | { type: "START" }
    | { type: "FAIL"; message: string }
    | { type: "DISMISS_ERROR" }

const INITIAL_UI: CloseAccountUi = { confirmVisible: false, closing: false, closeError: null }

function closeAccountUiReducer(state: CloseAccountUi, action: CloseAccountAction): CloseAccountUi {
    switch (action.type) {
        case "ASK":
            return { ...state, confirmVisible: true }
        case "CANCEL":
            return { ...state, confirmVisible: false }
        case "START":
            return { confirmVisible: false, closing: true, closeError: null }
        case "FAIL":
            return { ...state, closing: false, closeError: action.message }
        case "DISMISS_ERROR":
            return { ...state, closeError: null }
    }
}

export function useStudentCloseAccountState() {
    const onBack = useBackOr("settings", SETTINGS_ACCOUNT_TAB)
    const [ui, dispatchUi] = useReducer(closeAccountUiReducer, INITIAL_UI)
    const expoToken = useSelector(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state: any) => (state?.data?.session?.object?.expo_token as string | null | undefined) ?? null
    )

    const fail = useCallback((message: string) => dispatchUi({ type: "FAIL", message }), [])

    const { start: startDelete } = useApiMutation({
        service: authService.deleteAccount,
        voice: "student",
        fallback: CLOSE_FAILED,
        onError: fail,
    })

    const { start: startUnsubscribe } = useApiMutation({
        service: authService.unsubscribeFromNotifications,
        voice: "student",
        fallback: CLOSE_FAILED,
        onSuccess: () => startDelete(),
        onError: (message, error) => {
            if ((error as { status?: unknown } | null)?.status === "failed") startDelete()
            else fail(message)
        },
    })

    const close = useCallback(() => {
        if (expoToken) startUnsubscribe({}, { expo_token: expoToken })
        else startDelete()
    }, [expoToken, startUnsubscribe, startDelete])

    const onConfirmClose = useCallback(() => {
        if (ui.closing) return
        trackDialogButton(CLOSE_DIALOG_TITLE, "Supprimer mon compte")
        dispatchUi({ type: "START" })
        if (Platform.OS === "web") close()
        else void Notifications.dismissAllNotificationsAsync().then(close, close)
    }, [ui.closing, close])

    return {
        onBack,
        onAskClose: () => dispatchUi({ type: "ASK" }),
        confirmVisible: ui.confirmVisible,
        onConfirmClose,
        onCancelClose: () => {
            trackDialogButton(CLOSE_DIALOG_TITLE, "Annuler")
            dispatchUi({ type: "CANCEL" })
        },
        // Le voile ou le retour ferment sans mesurer : l'ancienne boîte ne mesurait que ses boutons.
        onDismissClose: () => dispatchUi({ type: "CANCEL" }),
        closing: ui.closing,
        closeError: ui.closeError,
        onDismissCloseError: () => dispatchUi({ type: "DISMISS_ERROR" }),
    }
}
